import { useState } from "react";

import type { AnyObject, ColumnsType } from "@similarweb/pro-logic-components";

import { useSearchAdvertisingDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useSearchAdvertisingDataAndParams";
import {
    getDomainWithSubdomain,
    getUrlComponents,
} from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import { CompareTabs } from "app/components/compareTabs/compareTabs";
import type { IAddToDashboardConfig } from "app/components/React/WidgetContainer/types";
import type { ISortProps } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { SearchAdsTable } from "./search-ads-table/SearchAdsTable";
import type { IAdData } from "app/common/keyword-research/website-search-ads/searchAdsTableTypes";
import { getSiteData } from "./searchAdsWebsiteUtils";

interface ICompareTable {
    data: IAdData[];
    isTableLoading: boolean;
    visibleColumns: string[];
    isTableError: boolean;
    handleSort: (sorter: ISortProps) => void;
    sortConfig: { sort: string; asc: boolean };
    headerTooltipKey: string;
    totalCount?: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    addToDashboardConfig?: IAddToDashboardConfig;
    excelUrl?: string;
    requestBodyForExcel?: any;
    getColumnsFunction?: (
        visibleColumns: string[],
        currentPage: number,
        pageSize: number,
        sortConfig?: { sort: string; asc: boolean },
        toggleExpandRow?: (key: number) => void,
        total?: number,
    ) => ColumnsType<AnyObject>;
}

export const CompareTable = (props: ICompareTable) => {
    const {
        data = [],
        isTableLoading,
        visibleColumns,
        isTableError,
        handleSort,
        sortConfig,
        totalCount,
        addToDashboardConfig,
        excelUrl,
        requestBodyForExcel,
        getColumnsFunction,
        ...rest
    } = props;
    const { selectedSite, pageFilter } = useSearchAdvertisingDataAndParams();
    const pagesParam = JSON.parse(pageFilter ?? "[]");

    const [selectedSiteTabIndex, setSelectedSiteTabIndex] = useState(() => {
        const selectedSiteIndex = pagesParam.findIndex((page) => page.url === selectedSite);
        return selectedSiteIndex > 0 ? selectedSiteIndex : 0;
    });

    const siteData = getSiteData(pageFilter);

    const onSiteTabSelect = (index: number) => {
        setSelectedSiteTabIndex(index);
        const { domain, subdomain } = getUrlComponents(siteData?.[index]?.title);
        const updatedSelectedSite = getDomainWithSubdomain(domain, subdomain);
        proNavigator.updateParams({
            selectedSite: updatedSelectedSite,
        });
    };

    const addToDashboardConfigWithSelectedSite = {
        ...addToDashboardConfig,
        selectedSite: true,
    };

    return (
        <CompareTabs
            tabs={siteData ?? []}
            tabSelectedIndex={selectedSiteTabIndex}
            onTabSelectedCallback={onSiteTabSelect}
            isLoading={false}
            hideResults={true}
            tabPanels={
                <SearchAdsTable<IAdData>
                    data={data}
                    isLoading={isTableLoading}
                    visibleColumns={visibleColumns}
                    isError={isTableError}
                    sortConfig={sortConfig}
                    handleSort={handleSort}
                    totalCount={totalCount}
                    addToDashboardConfig={addToDashboardConfigWithSelectedSite}
                    excelUrl={excelUrl}
                    requestBodyForExcel={requestBodyForExcel}
                    getColumnsFunction={getColumnsFunction}
                    {...rest}
                />
            }
        />
    );
};
